import React, { useEffect, useState, useRef } from 'react';
import './../Styles/Admin.css';
import './../Styles/Employee.css';
import './../Styles/LeadDashboard.css';
import logo from './../Images/tsqs-hm-ft-logo1.gif';
import axios from 'axios';
import CreateGoal from './CreateGoal';
import AssignGoal from './AssignGoal';
import { FaInfoCircle } from 'react-icons/fa'; 
import { faHistory } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useNavigate } from 'react-router-dom';
import { TailSpin } from 'react-loader-spinner';
import ProfileDialog from './ProfileDialog';
import LoadingOverlay from './LoadingOverlay';
import TextModal from './TextModal';
import { useToast } from './ToastContext';
import AppHeader from './AppHeader';

const apiUrl = process.env.REACT_APP_API_URL;

const LeadDashboard = () => {
    const showToast = useToast();
    const navigate = useNavigate();

    // Section state: 'assigned' (Assigned Goals) or 'reviewed' (Reviewed Goals)
    const VALID_LEAD_SECTIONS = ['assigned', 'reviewed'];
    const [currentSection, setCurrentSection] = useState(() => {
        const savedSection = localStorage.getItem('lead_currentSection');
        return VALID_LEAD_SECTIONS.includes(savedSection) ? savedSection : 'assigned';
    });

    useEffect(() => {
        if (VALID_LEAD_SECTIONS.includes(currentSection)) {
            localStorage.setItem('lead_currentSection', currentSection);
        }
    }, [currentSection]);

    // User & Team state
    const [managerInfo, setManagerInfo] = useState({ full_name: '', job_title: '', employee_number: '' });
    const [teamEmployees, setTeamEmployees] = useState([]);
    const [selectedEmployee, setSelectedEmployee] = useState(null);
    const [employeeDetails, setEmployeeDetails] = useState([]);
    const [noGoalsMessage, setNoGoalsMessage] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Profile state
    const [isProfileDialogOpen, setProfileDialogOpen] = useState(false);

    // Goal submission for team members (Reviewed Goals view)
    const [reviewerCommentError, setReviewerCommentError] = useState(false);

    // ── ASSIGNED GOALS STATE (Lead as an employee) ──
    const [assignedGoals, setAssignedGoals] = useState([]);
    const [quarters, setQuarters] = useState([]);
    const [selectedQuarter, setSelectedQuarter] = useState('');
    const [goalDescriptions, setGoalDescriptions] = useState({});
    const [attachmentLinks, setAttachmentLinks] = useState({});
    const [selfRatings, setSelfRatings] = useState({});
    const [communicatedStatuses, setCommunicatedStatuses] = useState({});
    const [errors, setErrors] = useState({});
    const [loadingGoalId, setLoadingGoalId] = useState(null);
    const [isInstructionsOpen, setInstructionsOpen] = useState(false);

    // TextModal State
    const [modalIsOpen, setModalIsOpen] = useState(false);
    const [currentField, setCurrentField] = useState(null);
    const [currentGoalId, setCurrentGoalId] = useState(null);
    const [initialText, setInitialText] = useState('');

    // Fetch initial lead info & quarters
    useEffect(() => {
        const leadEmpId = localStorage.getItem('employeeId');
        if (!leadEmpId) {
            console.error('Lead Employee ID not found in local storage.');
            return;
        }

        // Fetch lead user info
        axios.get(`${apiUrl}/api/manager-info/${leadEmpId}`)
            .then(response => {
                setManagerInfo(response.data);
            })
            .catch(err => console.error('Error fetching lead info:', err));

        // Fetch quarters for assigned goals dropdown
        axios.get(`${apiUrl}/api/quarters`)
            .then(response => {
                const qs = (response.data || []).map(q => q.quarter);
                setQuarters(qs);
                if (qs.length > 0) setSelectedQuarter(qs[0]);
            })
            .catch(err => console.error('Error fetching quarters:', err));

        // Fetch lead's assigned goals
        fetchAssignedGoals(leadEmpId);

        // Fetch team members under this lead (for Reviewed Goals section)
        axios.get(`${apiUrl}/api/lead-id/${leadEmpId}`)
            .then(res => {
                const leadId = res.data.lead_id;
                return axios.get(`${apiUrl}/api/employees-by-lead/${leadId}`);
            })
            .then(res => {
                setTeamEmployees(res.data || []);
            })
            .catch(err => console.error('Error fetching team employees:', err));
    }, []);

    // Fetch lead's assigned goals
    const fetchAssignedGoals = async (leadEmpId) => {
        try {
            const response = await axios.get(`${apiUrl}/api/employee-goals/${leadEmpId}`);
            const uniqueGoals = Array.from(new Map(response.data.map(goal => [goal._id || goal.id, goal])).values());
            setAssignedGoals(uniqueGoals);
        } catch (err) {
            console.error('Error fetching lead assigned goals:', err);
        }
    };

    // TextModal controls for Assigned Goals inputs
    const openTextModal = (field, goalId, existingText) => {
        setCurrentField(field);
        setCurrentGoalId(goalId);
        if (field === 'description') {
            setInitialText(goalDescriptions[goalId] || existingText || '');
        } else if (field === 'attachment') {
            setInitialText(attachmentLinks[goalId] || existingText || '');
        }
        setModalIsOpen(true);
    };

    const handleTextModalSubmit = (submittedText) => {
        if (currentField === 'description' && currentGoalId !== null) {
            setGoalDescriptions(prev => ({ ...prev, [currentGoalId]: submittedText }));
        } else if (currentField === 'attachment' && currentGoalId !== null) {
            setAttachmentLinks(prev => ({ ...prev, [currentGoalId]: submittedText }));
        }
        setModalIsOpen(false);
    };

    // Submit a single assigned goal row
    const handleSubmitAssignedGoalRow = async (goalId) => {
        const leadEmpId = localStorage.getItem('employeeId');

        if (!selectedQuarter) {
            showToast('Please select a quarter before submitting', 'warning');
            return;
        }

        const description = (goalDescriptions[goalId] || '').trim();
        const rating = selfRatings[goalId] || '';
        const communicated = communicatedStatuses[goalId] || '';

        let newErrors = {};
        if (!description) {
            newErrors[goalId] = 'Description text is required';
            showToast('Description text is mandatory for submission.', 'warning');
            setErrors(newErrors);
            return;
        }
        if (!rating) {
            newErrors[goalId] = 'Self rating is required';
            showToast('Self Rating is mandatory for submission.', 'warning');
            setErrors(newErrors);
            return;
        }
        if (!communicated) {
            newErrors[goalId] = 'Communicated status is required';
            showToast('Communicated status is mandatory for submission.', 'warning');
            setErrors(newErrors);
            return;
        }

        setErrors({});
        setLoadingGoalId(goalId);
        try {
            await axios.post(`${apiUrl}/api/update-goal-row`, {
                goalId,
                self_assessment: goalDescriptions[goalId] || '',
                attachments: attachmentLinks[goalId] || '',
                self_rating: selfRatings[goalId] || '',
                communicated: communicatedStatuses[goalId] || '',
                employeeId: leadEmpId,
                quarter: selectedQuarter,
                is_submitted: true
            });
            setAssignedGoals(prev => prev.filter(g => (g._id || g.id) !== goalId));
            showToast('Goal submitted successfully.', 'success');
        } catch (err) {
            if (err.response && err.response.status === 400) {
                showToast('Goal has already been submitted for this quarter.', 'error');
            } else {
                showToast('Error updating goal.', 'error');
            }
        } finally {
            setLoadingGoalId(null);
        }
    };

    // ── TEAM MEMBER REVIEW HANDLERS (Reviewed Goals view) ──
    const handleViewDetails = (employee, quarter = selectedQuarter) => {
        setEmployeeDetails([]);
        setNoGoalsMessage('');
        setSelectedEmployee(employee);
        axios.get(`${apiUrl}/api/employee-admin-goals/${employee.employee_number}/${encodeURIComponent(quarter)}`)
            .then(res => {
                if (res.data.length === 0) {
                    setEmployeeDetails([]);
                    setNoGoalsMessage(`No goals submitted for ${employee.employee_number} in ${quarter}.`);
                } else {
                    setEmployeeDetails(res.data || []);
                    setNoGoalsMessage('');
                }
            })
            .catch(err => {
                if (err.response && err.response.status === 404) {
                    setEmployeeDetails([]);
                    setNoGoalsMessage(`No goals submitted for ${employee.employee_number} in ${quarter}.`);
                } else {
                    console.error('Error fetching employee details:', err);
                }
            });
    };

    const handleReviewerRatingChange = (goalId, newRating) => {
        setEmployeeDetails(prevDetails =>
            prevDetails.map(detail =>
                detail.goal_id === goalId ? { ...detail, reviewer_rating: newRating } : detail
            )
        );
    };

    const handleSubmitTeamReview = async () => {
        setLoading(true);
        const leadId = localStorage.getItem('employeeId');

        if (!leadId || !selectedEmployee) return;

        let hasError = false;
        const updatedEmployeeDetails = employeeDetails.map((detail) => {
            const upd = { ...detail };
            if (!upd.reviewer_rating) {
                upd.reviewerRatingError = 'Reviewer rating is required';
                hasError = true;
            } else {
                upd.reviewerRatingError = null;
            }
            return upd;
        });

        if (!employeeDetails[0]?.reviewer_comment) {
            setReviewerCommentError('*Reviewer Comment is required');
            hasError = true;
        } else {
            setReviewerCommentError(null);
        }

        if (hasError) {
            setEmployeeDetails(updatedEmployeeDetails);
            setLoading(false);
            return;
        }

        try {
            const goalReviewsData = {
                goals: employeeDetails.map(detail => ({
                    goalId: detail.goal_id,
                    reviewerRating: detail.reviewer_rating,
                })),
                employeeId: selectedEmployee.employee_number,
            };

            await axios.post(`${apiUrl}/api/submit-goal-reviews`, goalReviewsData);

            const finalReviewData = {
                employeeId: selectedEmployee.employee_number,
                leadId: leadId, 
                reviewerComment: employeeDetails[0]?.reviewer_comment,
                quarter: selectedQuarter
            };

            await axios.post(`${apiUrl}/api/notify-manager-review`, finalReviewData);

            setLoading(false);
            showToast('Submitted successfully!', 'success');
            setSelectedEmployee(null);
        } catch (err) {
            console.error('Error submitting data:', err);
            setLoading(false);
            showToast('Error while submitting data. Please try again.', 'error');
        }
    };

    // Filter team employees based on search
    const filteredTeamEmployees = teamEmployees.filter(emp =>
        emp.full_name && emp.full_name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Profile Dialog toggles
    const handleOpenProfileDialog = () => setProfileDialogOpen(true);
    const handleCloseProfileDialog = () => setProfileDialogOpen(false);

    // Render team member details dialog
    const renderDetailsDialog = () => {
        if (!selectedEmployee) return null;

        return (
            <div className="details-dialog-overlay">
                <div className="details-dialog">
                    <h2>Details of {selectedEmployee.full_name}</h2>
                    <div className="quarter-selector">
                        <label htmlFor="quarter">Select Quarter:</label>
                        <select
                            id="quarter"
                            value={selectedQuarter}
                            onChange={(e) => {
                                setSelectedQuarter(e.target.value);
                                handleViewDetails(selectedEmployee, e.target.value);
                            }}
                        >
                            {quarters.map(q => (
                                <option key={q} value={q}>{q}</option>
                            ))}
                        </select>
                    </div>

                    <div className="employee-summary">
                        <div className="summary-item">
                            <p><strong>Full Name:</strong> {selectedEmployee.full_name}</p>
                        </div>
                        <div className="summary-item">
                            <p><strong>Location:</strong> {selectedEmployee.location || 'N/A'}</p>
                        </div>
                        <div className="summary-item">
                            <p><strong>Job Title:</strong> {selectedEmployee.job_title}</p>
                        </div>
                        <div className="summary-item">
                            <p><strong>Department:</strong> {selectedEmployee.department || 'N/A'}</p>
                        </div>
                    </div>
                    {employeeDetails.length > 0 ? (
                        <>
                            <table className="details-table">
                                <thead>
                                    <tr>
                                        <th>Goal Description</th>
                                        <th>Self Assessment</th>
                                        <th>Self Rating</th>
                                        <th>Communicated</th>
                                        <th>Attachments</th>
                                        <th>Reviewer Rating</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {employeeDetails.map((detail) => (
                                        <tr key={detail.id || detail.goal_id}>
                                            <td className="tabledisabled">{detail.goal_description}</td>
                                            <td className="tabledisabled">{detail.self_assessment || 'N/A'}</td>
                                            <td className="tabledisabled">{detail.self_rating || 'N/A'}</td>
                                            <td className="tabledisabled">{detail.communicated ? 'Yes' : 'No'}</td>
                                            <td className="tabledisabled">
                                                {detail.attachments ? (
                                                    <a href={detail.attachments} target="_blank" className="attachment-link" rel="noopener noreferrer">
                                                        View Attachment
                                                    </a>
                                                ) : 'None'}
                                            </td>
                                            <td>
                                                <select
                                                    value={detail.reviewer_rating || ''}
                                                    onChange={(e) => handleReviewerRatingChange(detail.goal_id, e.target.value)}
                                                >
                                                    <option value="" disabled>Select</option>
                                                    {[1, 2, 3, 4, 5].map(r => (
                                                        <option key={r} value={r}>{r}</option>
                                                    ))}
                                                </select>
                                                {detail.reviewerRatingError && (
                                                    <div className="error-message">{detail.reviewerRatingError}</div>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                            <div className="reviewer-comment-section">
                                <strong>Reviewer Comment:</strong>
                                <textarea 
                                    value={employeeDetails[0]?.reviewer_comment || ''} 
                                    onChange={(e) => setEmployeeDetails(prevDetails => {
                                        const updatedDetails = [...prevDetails];
                                        updatedDetails[0].reviewer_comment = e.target.value;
                                        return updatedDetails;
                                    })}
                                />
                                {reviewerCommentError && (
                                    <div className="error-message">{reviewerCommentError}</div>
                                )}
                            </div>
                        </>
                    ) : (
                        <p>{noGoalsMessage || 'No goals found for this employee.'}</p>
                    )}

                    {loading && (
                        <div className="overlay-loading">
                            <div className="overlay-content-loading">
                                <TailSpin height="40" width="40" color="#00BFFF" />
                                <span className="loading-text-loading">Submitting Review...</span>
                            </div>
                        </div>
                    )}

                    <div className="button-group" style={{ marginTop: '16px', display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                        <button className="close-button" onClick={() => setSelectedEmployee(null)}>Close</button>
                        <button className="sort-button" onClick={handleSubmitTeamReview}>Submit</button>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="lead-dashboard-page">
            {/* Full Width Top Header spanning 100% across the top of screen */}
            <AppHeader
                title="PERFORMANCE MANAGEMENT"
                employeeInfo={managerInfo}
                onProfileClick={handleOpenProfileDialog}
                hideLogo={false}
            />

            {/* Layout Body containing Left Sidebar and Right Main Content */}
            <div className="lead-body-wrapper">
                {/* Fixed Left Navigation Bar (below top header) */}
                <aside className="lead-sidebar">
                    <nav className="nav-menu">
                        <a
                            href="#"
                            className={`nav-item ${currentSection === 'assigned' ? 'active' : ''}`}
                            onClick={(e) => { e.preventDefault(); setCurrentSection('assigned'); }}
                        >
                            Assigned Goals
                        </a>
                        <a
                            href="#"
                            className={`nav-item ${currentSection === 'reviewed' ? 'active' : ''}`}
                            onClick={(e) => { e.preventDefault(); setCurrentSection('reviewed'); }}
                        >
                            Employees
                        </a>
                    </nav>
                </aside>

                {/* Main Content Area (right side of fixed navbar) */}
                <main className="lead-main-content">
                    <div className="welcome-section">
                        <h1>Hi {managerInfo.full_name ? managerInfo.full_name.split(' ')[0] : 'Lead'}...</h1>
                    </div>

                    {/* ── SECTION 1: ASSIGNED GOALS VIEW (Matches Employee Page Screenshot) ── */}
                    {currentSection === 'assigned' && (
                        <div className="employee-body">
                            {loadingGoalId && <LoadingOverlay />}
                            <div className="goals-header-container">
                                <h2>Assigned Goals</h2>
                                <div className="quarter-history-container">
                                    <div className="quarter-selector">
                                        <label htmlFor="quarter">Select Quarter <span className="required-star" style={{ color: 'red' }}>*</span>:</label>
                                        <select
                                            id="quarter"
                                            value={selectedQuarter}
                                            onChange={(e) => setSelectedQuarter(e.target.value)}
                                        >
                                            <option value="">Select Quarter</option>
                                            {quarters.map((quarter) => (
                                                <option key={quarter.id || quarter._id || quarter.quarter} value={quarter.quarter}>
                                                    {quarter.quarter}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <button className="history-btn" onClick={() => navigate('/history')}>
                                        <FontAwesomeIcon icon={faHistory} /> Submitted
                                    </button>
                                    <button className="instructions-btn" onClick={() => setInstructionsOpen(true)} title="Instructions">
                                        <FaInfoCircle size={24} />
                                    </button>
                                </div>
                            </div>

                            <div className="table-container pms-table-wrap">
                                <table className="goals-table pms-table">
                                    <thead>
                                        <tr>
                                            <th>GOAL DESCRIPTION</th>
                                            <th>DESCRIPTION TEXT <span className="required-star" style={{ color: 'red' }}>*</span></th>
                                            <th>ATTACHMENT</th>
                                            <th>SELF RATING <span className="required-star" style={{ color: 'red' }}>*</span></th>
                                            <th>COMMUNICATED <span className="required-star" style={{ color: 'red' }}>*</span></th>
                                            <th>SUBMIT</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {assignedGoals.length > 0 ? (
                                            assignedGoals.map((goal) => {
                                                const gId = goal._id || goal.id;
                                                return (
                                                    <tr key={gId}>
                                                        <td style={{ fontWeight: '500' }}>{goal.goal_description || 'N/A'}</td>
                                                        <td>
                                                            <input 
                                                                type="text"
                                                                value={goalDescriptions[gId] || ''}
                                                                onChange={(e) => setGoalDescriptions(prev => ({ ...prev, [gId]: e.target.value }))}
                                                                onClick={() => openTextModal('description', gId, goalDescriptions[gId] || '')}
                                                                placeholder="Enter description"
                                                            />
                                                            {errors[gId] && <span className="error-message">{errors[gId]}</span>}
                                                        </td>
                                                        <td>
                                                            <input 
                                                                type="text"
                                                                value={attachmentLinks[gId] || ''}
                                                                onChange={(e) => setAttachmentLinks(prev => ({ ...prev, [gId]: e.target.value }))}
                                                                onClick={() => openTextModal('attachment', gId, attachmentLinks[gId] || '')}
                                                                placeholder="Enter attachment link"
                                                            />
                                                        </td>
                                                        <td>
                                                            <select
                                                                value={selfRatings[gId] || ''}
                                                                onChange={(e) => setSelfRatings(prev => ({ ...prev, [gId]: e.target.value }))}
                                                            >
                                                                <option value="">Select</option>
                                                                <option value="1">1</option>
                                                                <option value="2">2</option>
                                                                <option value="3">3</option>
                                                                <option value="4">4</option>
                                                                <option value="5">5</option>
                                                            </select>
                                                            {errors[gId] && <span className="error-message">{errors[gId]}</span>}
                                                        </td>
                                                        <td>
                                                            <select
                                                                value={communicatedStatuses[gId] || ''}
                                                                onChange={(e) => setCommunicatedStatuses(prev => ({ ...prev, [gId]: e.target.value }))}
                                                            >
                                                                <option value="">Select</option>
                                                                <option value="yes">Yes</option>
                                                                <option value="no">No</option>
                                                            </select>
                                                            {errors[gId] && <span className="error-message">{errors[gId]}</span>}
                                                        </td>
                                                        <td>
                                                            <button 
                                                                onClick={() => handleSubmitAssignedGoalRow(gId)} 
                                                                disabled={loadingGoalId === gId}
                                                                style={{
                                                                    backgroundColor: '#e67320',
                                                                    color: '#ffffff',
                                                                    border: 'none',
                                                                    padding: '6px 18px',
                                                                    borderRadius: '20px',
                                                                    fontWeight: '600',
                                                                    cursor: 'pointer'
                                                                }}
                                                            >
                                                                {loadingGoalId === gId ? 'Submitting...' : 'Submit'}
                                                            </button>
                                                        </td>
                                                    </tr>
                                                );
                                            })
                                        ) : (
                                            <tr>
                                                <td colSpan="6" style={{ textAlign: 'center', padding: '24px', color: 'var(--text-secondary)' }}>
                                                    No assigned goals found for your account.
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>

                            {/* TextModal for editing long description/attachment */}
                            <TextModal
                                isOpen={modalIsOpen}
                                onRequestClose={() => setModalIsOpen(false)}
                                onTextSubmit={handleTextModalSubmit}
                                initialText={initialText}
                                field={currentField}
                            />

                            {/* Instructions Modal */}
                            <div className={`instructions-modal ${isInstructionsOpen ? 'open' : ''}`}>
                                <h3>Goal Submission and Review Process</h3>
                                <ol>
                                    <li>Goals will be dynamically fetched in your dashboard once your manager assigns goals to you.</li>
                                    <li>You need to select the particular quarter from the dropdown for which you are submitting the goal.</li>
                                    <li>When entering your goal description, write it in a notepad or click the text input to use the full text editor.</li>
                                    <li>To upload certifications or attachments, store the files in OneDrive and paste the shared link into the "Attachment" field.</li>
                                    <li>After submitting a goal, your manager will be notified automatically.</li>
                                    <li>You can view all submitted goals in the "Submitted" section.</li>
                                    <li>If you encounter any issues, please reach out to <a href="mailto:pms@tsqsinc.com">pms@tsqsinc.com</a> for assistance.</li>
                                </ol>
                                <button className="close-instructions" onClick={() => setInstructionsOpen(false)}>Close</button>
                            </div>
                        </div>
                    )}

                    {/* ── SECTION 2: REVIEWED GOALS VIEW (Team Member Goal Reviews) ── */}
                    {currentSection === 'reviewed' && (
                        <div>
                            <div className="employee-list-section">
                                <div className="employee-controls">
                                    <div className="controls-left">
                                        <h2>List of Team Employees</h2>
                                    </div>
                                    <div className="controls-right">
                                        <input 
                                            type="text" 
                                            placeholder="Search Employees..." 
                                            className="search-bar"  
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)} 
                                        />
                                        <button className="sort-button">Filter</button>
                                    </div>
                                </div>

                                <div className="employee-table-wrapper">
                                    <table className="employee-table">
                                        <thead>
                                            <tr>
                                                <th>Employee Number</th>
                                                <th>Full Name</th>
                                                <th>Job Title</th>
                                                <th>Project</th>
                                                <th>Details</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {filteredTeamEmployees.length > 0 ? (
                                                filteredTeamEmployees.map(employee => (
                                                    <tr key={employee.employee_number}>
                                                        <td>{employee.employee_number || 'N/A'}</td>
                                                        <td>{employee.full_name || 'N/A'}</td>
                                                        <td>{employee.job_title || 'N/A'}</td>
                                                        <td>{employee.project_name || 'N/A'}</td>
                                                        <td>
                                                            <button 
                                                                className="details-button" 
                                                                onClick={() => handleViewDetails(employee, selectedQuarter)}
                                                            >
                                                                View
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td colSpan="5" style={{ textAlign: 'center', padding: '20px' }}>
                                                        No team employees found.
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>

                        </div>
                    )}
                </main>
            </div>

            {/* Team details review dialog */}
            {renderDetailsDialog()}

            {/* Profile Dialog */}
            {isProfileDialogOpen && (
                <ProfileDialog
                    employeeInfo={managerInfo}
                    onClose={handleCloseProfileDialog}
                    onUpdate={(updatedData) => setManagerInfo(prev => ({ ...prev, ...updatedData }))}
                />
            )}
        </div>
    );
};

export default LeadDashboard;

